import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/modules/products/hooks/useProducts';
import { usePlans } from '@/modules/plans/hooks/usePlans';
import { useCreateSubscription } from '../hooks/useCreateSubscription';
import { useCheckoutSession } from '../hooks/useCheckoutSession';
import {
  useCreatePaymentIntent,
  useConfirmPaymentIntent,
} from '@/modules/payments/hooks/usePayments';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Alert } from '@/shared/components/ui/alert';
import { cn } from '@/shared/lib/cn';
import { APP_ROUTES } from '@/shared/config/app-routes';
import type { ProductResponseDTO } from '@/modules/products/types';
import type { PlanResponseDTO } from '@/modules/plans/types';
import type { SubscriptionResponseDTO } from '../types';

// ─── Search param types ───────────────────────────────────────────────────────

export type WizardStep = 'product' | 'plan' | 'review' | 'payment' | 'success';

export interface WizardSearch {
  step?: WizardStep | undefined;
  product_id?: string | undefined;
  plan_id?: string | undefined;
  subscription_id?: string | undefined;
  checkout_session?: string | undefined;
}

// UUID v4 validation
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUuid(v: string | undefined): v is string {
  return !!v && UUID_RE.test(v);
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS: WizardStep[] = ['product', 'plan', 'review', 'payment', 'success'];

function StepIndicator({ current }: { current: WizardStep }) {
  const { t } = useTranslation();
  const currentIdx = STEPS.indexOf(current);
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((step, i) => (
        <div key={step} className="flex items-center gap-2">
          <div
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 transition-colors',
              i < currentIdx
                ? 'bg-primary border-primary text-primary-foreground'
                : i === currentIdx
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-muted text-muted-foreground bg-transparent',
            )}
          >
            {i + 1}
          </div>
          <span
            className={cn(
              'text-xs font-medium hidden sm:block',
              i === currentIdx ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            {t(`subscriptions.wizard.steps.${step}`)}
          </span>
          {i < STEPS.length - 1 && (
            <div className={cn('h-px w-6 mx-1', i < currentIdx ? 'bg-primary' : 'bg-muted')} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Step 1: Product selection ────────────────────────────────────────────────

function StepProduct({ onSelect }: { onSelect: (productId: string) => void }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const { data, isLoading, error } = useProducts({ is_active: true, limit: 50 });

  if (isLoading) return <p className="text-muted-foreground text-sm">{t('common.loading')}</p>;
  if (error) return <Alert>{t('common.error')}</Alert>;

  const products = data?.items ?? [];

  if (products.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('subscriptions.wizard.noProducts')}</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('subscriptions.wizard.selectProduct')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((product: ProductResponseDTO) => (
          <button
            key={product.id}
            type="button"
            onClick={() => onSelect(product.id)}
            className="text-left rounded-lg border-2 border-transparent hover:border-primary focus:border-primary focus:outline-none transition-colors p-0"
          >
            <Card className="h-full cursor-pointer hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {isAr ? product.name_ar : product.name_en}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {product.description_en && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {isAr ? product.description_en : product.description_en}
                  </p>
                )}
                <p className="text-xs text-muted-foreground font-mono mt-1">{product.slug}</p>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 2: Plan selection ───────────────────────────────────────────────────

function StepPlan({
  productId,
  onSelect,
  onBack,
}: {
  productId: string;
  onSelect: (planId: string) => void;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const { data, isLoading, error } = usePlans({ product_id: productId, is_active: true });

  if (isLoading) return <p className="text-muted-foreground text-sm">{t('common.loading')}</p>;
  if (error) return <Alert>{t('common.error')}</Alert>;

  const plans = data?.items ?? [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('subscriptions.wizard.selectPlan')}</p>
      {plans.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('subscriptions.wizard.noPlans')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan: PlanResponseDTO) => (
            <button
              key={plan.id}
              type="button"
              onClick={() => onSelect(plan.id)}
              className="text-left rounded-lg border-2 border-transparent hover:border-primary focus:border-primary focus:outline-none transition-colors p-0"
            >
              <Card className="h-full cursor-pointer hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{isAr ? plan.name_ar : plan.name_en}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold">
                      {Number(plan.price_amount).toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">{plan.currency}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(`plans.periods.${plan.billing_period}`)}
                  </p>
                  {plan.trial_days > 0 && (
                    <p className="text-xs text-primary">
                      {t('subscriptions.wizard.review.trial', { days: plan.trial_days })}
                    </p>
                  )}
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}
      <Button variant="outline" size="sm" onClick={onBack}>
        {t('subscriptions.wizard.back')}
      </Button>
    </div>
  );
}

// ─── Step 3: Review + confirm ─────────────────────────────────────────────────

function StepReview({
  productId,
  planId,
  onConfirm,
  onBack,
  isLoading,
  error,
}: {
  productId: string;
  planId: string;
  onConfirm: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const { data: productsData } = useProducts({ is_active: true, limit: 100 });
  const { data: plansData } = usePlans({ product_id: productId, is_active: true });

  const product = productsData?.items.find((p: ProductResponseDTO) => p.id === productId);
  const plan = plansData?.items.find((p: PlanResponseDTO) => p.id === planId);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">{t('subscriptions.wizard.review.title')}</p>
      <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t('subscriptions.wizard.review.product')}</span>
          <span className="font-medium">
            {product ? (isAr ? product.name_ar : product.name_en) : productId}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t('subscriptions.wizard.review.plan')}</span>
          <span className="font-medium">
            {plan ? (isAr ? plan.name_ar : plan.name_en) : planId}
          </span>
        </div>
        {plan && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {t('subscriptions.wizard.review.billing')}
              </span>
              <span className="font-medium">{t(`plans.periods.${plan.billing_period}`)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {t('subscriptions.wizard.review.price')}
              </span>
              <span className="font-medium">
                {Number(plan.price_amount).toLocaleString()} {plan.currency}
              </span>
            </div>
            {plan.trial_days > 0 && (
              <p className="text-xs text-primary">
                {t('subscriptions.wizard.review.trial', { days: plan.trial_days })}
              </p>
            )}
          </>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="flex gap-3">
        <Button variant="outline" size="sm" onClick={onBack} disabled={isLoading}>
          {t('subscriptions.wizard.back')}
        </Button>
        <Button onClick={onConfirm} disabled={isLoading}>
          {isLoading ? t('subscriptions.wizard.subscribing') : t('subscriptions.wizard.subscribe')}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 4: Payment ─────────────────────────────────────────────────────────

function StepPayment({
  subscriptionId,
  onSuccess,
  onBack,
}: {
  subscriptionId: string;
  onSuccess: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const createIntent = useCreatePaymentIntent();
  const confirmIntent = useConfirmPaymentIntent();
  const initiated = useRef(false);

  useEffect(() => {
    if (!initiated.current) {
      initiated.current = true;
      createIntent.mutate({ subscriptionId, idempotencyKey: crypto.randomUUID() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePay = useCallback(() => {
    if (!createIntent.data) return;
    confirmIntent.mutate(createIntent.data.id, { onSuccess });
  }, [createIntent.data, confirmIntent, onSuccess]);

  if (createIntent.isPending || (!createIntent.data && !createIntent.error)) {
    return (
      <p className="text-sm text-muted-foreground">{t('subscriptions.wizard.payment.preparing')}</p>
    );
  }

  if (createIntent.error) {
    return (
      <div className="space-y-4">
        <Alert>{t('subscriptions.wizard.payment.error')}</Alert>
        <Button variant="outline" size="sm" onClick={onBack}>
          {t('subscriptions.wizard.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {t('subscriptions.wizard.payment.description')}
      </p>
      <div className="rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/20 p-6 space-y-4">
        <p className="text-xs text-muted-foreground text-center">
          {t('subscriptions.wizard.payment.mockCardPlaceholder')}
        </p>
        <div className="space-y-3 opacity-50 pointer-events-none select-none">
          <div className="h-9 rounded border bg-background" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-9 rounded border bg-background" />
            <div className="h-9 rounded border bg-background" />
          </div>
        </div>
      </div>
      {confirmIntent.error && <Alert>{t('subscriptions.wizard.payment.error')}</Alert>}
      <div className="flex gap-3">
        <Button variant="outline" size="sm" onClick={onBack} disabled={confirmIntent.isPending}>
          {t('subscriptions.wizard.back')}
        </Button>
        <Button onClick={handlePay} disabled={confirmIntent.isPending}>
          {confirmIntent.isPending
            ? t('subscriptions.wizard.payment.paying')
            : t('subscriptions.wizard.payment.payNow')}
        </Button>
      </div>
    </div>
  );
}

// ─── Step 5: Success ──────────────────────────────────────────────────────────

function StepSuccess({ subscription }: { subscription: SubscriptionResponseDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-4 text-center py-4">
      <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30">
        <svg
          className="w-6 h-6 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold">{t('subscriptions.wizard.success.title')}</h3>
      <p className="text-sm text-muted-foreground font-mono">
        {t('subscriptions.wizard.success.message', { id: subscription.id })}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        <Button variant="outline" onClick={() => void navigate({ to: '/dashboard/subscriptions' })}>
          {t('subscriptions.wizard.success.backToList')}
        </Button>
        <Button
          onClick={() =>
            void navigate({
              to: '/dashboard/subscriptions/$subscriptionId',
              params: { subscriptionId: subscription.id },
            })
          }
        >
          {t('subscriptions.wizard.success.viewSubscription')}
        </Button>
      </div>
    </div>
  );
}

// ─── Main wizard page ─────────────────────────────────────────────────────────

interface SubscribeFlowPageProps {
  search: WizardSearch;
}

export function SubscribeFlowPage({ search }: SubscribeFlowPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createSubscription = useCreateSubscription();
  const autoCheckoutStarted = useRef(false);

  const step: WizardStep = search.step ?? 'product';
  const productId = isUuid(search.product_id) ? search.product_id : undefined;
  const planId = isUuid(search.plan_id) ? search.plan_id : undefined;
  const subscriptionId = isUuid(search.subscription_id) ? search.subscription_id : undefined;
  const checkoutSession = isUuid(search.checkout_session) ? search.checkout_session : undefined;
  const checkoutSessionQuery = useCheckoutSession(checkoutSession);

  const goTo = useCallback(
    (nextSearch: WizardSearch) => {
      void navigate({
        to: APP_ROUTES.SUBSCRIBE_FLOW,
        search: nextSearch,
        replace: false,
      });
    },
    [navigate],
  );

  const handleSelectProduct = useCallback(
    (id: string) => goTo({ step: 'plan', product_id: id }),
    [goTo],
  );

  const handleSelectPlan = useCallback(
    (id: string) => {
      if (!productId) return;
      goTo({ step: 'review', product_id: productId, plan_id: id });
    },
    [goTo, productId],
  );

  const handleConfirm = useCallback(() => {
    if (!productId || !planId) return;
    createSubscription.mutate(
      {
        product_id: productId,
        plan_id: planId,
        ...(checkoutSession ? { checkout_session: checkoutSession } : {}),
      },
      {
        onSuccess: (subscription) => {
          void navigate({
            to: APP_ROUTES.SUBSCRIBE_FLOW,
            search: {
              step: 'payment',
              product_id: productId,
              plan_id: planId,
              subscription_id: subscription.id,
              ...(checkoutSession ? { checkout_session: checkoutSession } : {}),
            },
          });
        },
      },
    );
  }, [productId, planId, checkoutSession, createSubscription, navigate]);

  useEffect(() => {
    const session = checkoutSessionQuery.data;
    if (!checkoutSession || !session || autoCheckoutStarted.current) return;

    if (session.subscriptionId) {
      autoCheckoutStarted.current = true;
      void navigate({
        to: APP_ROUTES.SUBSCRIBE_FLOW,
        search: {
          step: 'payment',
          product_id: session.productId,
          plan_id: session.planId,
          subscription_id: session.subscriptionId,
          checkout_session: checkoutSession,
        },
        replace: true,
      });
      return;
    }

    autoCheckoutStarted.current = true;
    createSubscription.mutate(
      {
        product_id: session.productId,
        plan_id: session.planId,
        checkout_session: checkoutSession,
      },
      {
        onSuccess: (subscription) => {
          void navigate({
            to: APP_ROUTES.SUBSCRIBE_FLOW,
            search: {
              step: 'payment',
              product_id: session.productId,
              plan_id: session.planId,
              subscription_id: subscription.id,
              checkout_session: checkoutSession,
            },
            replace: true,
          });
        },
      },
    );
  }, [checkoutSession, checkoutSessionQuery.data, createSubscription, navigate]);

  const resolvedStep: WizardStep = (() => {
    if (step === 'success') return 'success';
    if (step === 'payment' && subscriptionId) return 'payment';
    if (step === 'review' && productId && planId) return 'review';
    if (step === 'plan' && productId) return 'plan';
    return 'product';
  })();

  const successSub = createSubscription.data;

  return (
    <div className="container mx-auto max-w-3xl py-8 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t('subscriptions.wizard.title')}</h1>
      </div>

      <StepIndicator current={resolvedStep} />

      {resolvedStep === 'success' && successSub ? (
        <StepSuccess subscription={successSub} />
      ) : resolvedStep === 'payment' && subscriptionId ? (
        <StepPayment
          subscriptionId={subscriptionId}
          onSuccess={() =>
            goTo({
              step: 'success',
              product_id: productId,
              plan_id: planId,
              subscription_id: subscriptionId,
            })
          }
          onBack={() => goTo({ step: 'review', product_id: productId, plan_id: planId })}
        />
      ) : resolvedStep === 'product' ? (
        <StepProduct onSelect={handleSelectProduct} />
      ) : resolvedStep === 'plan' && productId ? (
        <StepPlan
          productId={productId}
          onSelect={handleSelectPlan}
          onBack={() => goTo({ step: 'product' })}
        />
      ) : resolvedStep === 'review' && productId && planId ? (
        <StepReview
          productId={productId}
          planId={planId}
          onConfirm={handleConfirm}
          onBack={() => goTo({ step: 'plan', product_id: productId })}
          isLoading={createSubscription.isPending}
          error={createSubscription.error ? (createSubscription.error as Error).message : null}
        />
      ) : (
        <StepProduct onSelect={handleSelectProduct} />
      )}
    </div>
  );
}
